import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { supabase } from '../supabase';

const AVATARS = ['🌱','🦁','🐯','🦊','🐺','🦅','🐬','🦋','⭐','🔥','💎','🚀','🎯','🏆','💪','🧠','👑','🌊','🎸','🍀'];

const GOALS = [
  { emoji:'✈️', name:'Dream vacation', name_no:'Drømmeferie', name_sv:'Drömresa', name_da:'Drømmeferie', color:'#00D4FF' },
  { emoji:'📚', name:'Education', name_no:'Utdanning', name_sv:'Utbildning', name_da:'Uddannelse', color:'#B44FFF' },
  { emoji:'🛡️', name:'Emergency fund', name_no:'Nødfond', name_sv:'Nödfond', name_da:'Nødfond', color:'#22C97A' },
  { emoji:'🎯', name:'My own goal', name_no:'Mitt eget mål', name_sv:'Mitt eget mål', name_da:'Mit eget mål', color:'#F5C518' },
];

const T = {
  en: {
    step1Title:"What's your username?", step1Sub:"This is shown on the leaderboard",
    namePlaceholder:"Username", chooseAvatar:"Choose your avatar",
    step2Title:"Monthly income?", step2Sub:"After tax — what you actually receive",
    incomePlaceholder:"e.g. 22000",
    step2Hint:"per day before expenses",
    step2PayDay:"Payday (optional)", step2PayDayHint:"Day of month you get paid (1-31)",
    step3Title:"Fixed expenses per month?", step3Sub:"Rent, subscriptions, loans etc.",
    expensesPlaceholder:"e.g. 10000",
    step3Hint:"Your daily budget",
    step4Title:"What are you saving for?", step4Sub:"Choose your first goal",
    next:"Next →", skip:"Skip", back:"← Back", getStarted:"Get started →",
    wow:"Your daily budget is ready!",
    wowSub:"Based on your income and expenses",
    perDay:"/day", daysToPayday:"days until payday", perMonth:"over the whole month",
    letsGo:"Let's go! 🚀",
  },
  no: {
    step1Title:"Hva er brukernavnet ditt?", step1Sub:"Dette vises på leaderboardet",
    namePlaceholder:"Brukernavn", chooseAvatar:"Velg din avatar",
    step2Title:"Månedlig inntekt?", step2Sub:"Etter skatt — det du faktisk mottar",
    incomePlaceholder:"f.eks. 22000",
    step2Hint:"per dag før utgifter",
    step2PayDay:"Lønnsdag (valgfritt)", step2PayDayHint:"Dag i måneden du får lønn (1-31)",
    step3Title:"Faste utgifter per måned?", step3Sub:"Husleie, abonnementer, lån osv.",
    expensesPlaceholder:"f.eks. 10000",
    step3Hint:"Ditt daglige budsjett",
    step4Title:"Hva vil du spare til?", step4Sub:"Velg ditt første sparemål",
    next:"Neste →", skip:"Hopp over", back:"← Tilbake", getStarted:"Kom i gang →",
    wow:"Ditt daglige budsjett er klart!",
    wowSub:"Basert på inntekten og utgiftene dine",
    perDay:"/dag", daysToPayday:"dager til lønnsdag", perMonth:"over hele måneden",
    letsGo:"La oss starte! 🚀",
  },
  sv: {
    step1Title:"Vad är ditt användarnamn?", step1Sub:"Detta visas på topplistan",
    namePlaceholder:"Användarnamn", chooseAvatar:"Välj din avatar",
    step2Title:"Månadsinkomst?", step2Sub:"Efter skatt — vad du faktiskt får",
    incomePlaceholder:"t.ex. 22000",
    step2Hint:"per dag före utgifter",
    step2PayDay:"Lönningsdag (valfritt)", step2PayDayHint:"Dag i månaden du får lön (1-31)",
    step3Title:"Fasta utgifter per månad?", step3Sub:"Hyra, prenumerationer, lån osv.",
    expensesPlaceholder:"t.ex. 10000",
    step3Hint:"Din dagliga budget",
    step4Title:"Vad vill du spara till?", step4Sub:"Välj ditt första sparmål",
    next:"Nästa →", skip:"Hoppa över", back:"← Tillbaka", getStarted:"Kom igång →",
    wow:"Din dagliga budget är klar!",
    wowSub:"Baserat på din inkomst och dina utgifter",
    perDay:"/dag", daysToPayday:"dagar till lönningsdag", perMonth:"över hela månaden",
    letsGo:"Nu kör vi! 🚀",
  },
  da: {
    step1Title:"Hvad er dit brugernavn?", step1Sub:"Dette vises på ranglisten",
    namePlaceholder:"Brugernavn", chooseAvatar:"Vælg din avatar",
    step2Title:"Månedlig indkomst?", step2Sub:"Efter skat — hvad du faktisk modtager",
    incomePlaceholder:"f.eks. 22000",
    step2Hint:"pr. dag før udgifter",
    step2PayDay:"Lønningsdag (valgfrit)", step2PayDayHint:"Dag i måneden du får løn (1-31)",
    step3Title:"Faste udgifter pr. måned?", step3Sub:"Husleje, abonnementer, lån osv.",
    expensesPlaceholder:"f.eks. 10000",
    step3Hint:"Dit daglige budget",
    step4Title:"Hvad vil du spare til?", step4Sub:"Vælg dit første opsparingsmål",
    next:"Næste →", skip:"Spring over", back:"← Tilbage", getStarted:"Kom i gang →",
    wow:"Dit daglige budget er klar!",
    wowSub:"Baseret på din indkomst og dine udgifter",
    perDay:"/dag", daysToPayday:"dage til lønningsdag", perMonth:"over hele måneden",
    letsGo:"Lad os starte! 🚀",
  },
};

export default function OnboardingScreen({ onDone }) {
  const { state, dispatch } = useApp();
  const [step, setStep] = React.useState(0);
  const [visible, setVisible] = React.useState(true);
  const [name, setName] = React.useState('');
  const [avatar, setAvatar] = React.useState('🌱');
  const [chosenTheme, setChosenTheme] = React.useState('sapphire');
  const [income, setIncome] = React.useState('');
  const [expenses, setExpenses] = React.useState('');
  const [payDay, setPayDay] = React.useState('');
  const [selectedGoal, setSelectedGoal] = React.useState(null);
  const [goalName, setGoalName] = React.useState('');
  const [showCustomGoal, setShowCustomGoal] = React.useState(false);
  const [customGoalName, setCustomGoalName] = React.useState('');
  const [customGoalColor, setCustomGoalColor] = React.useState('#22C97A');
  const [finishing, setFinishing] = React.useState(false);
  const [showWow, setShowWow] = React.useState(false);

  const lang = (() => {
    try { const l = localStorage.getItem('savely_lang'); if (l) return l; } catch(e) {}
    return state?.language || 'en';
  })();

  const tx = T[lang] || T.en;
  const fmtAmt = (n) => {
    const currency = lang==='en'?'$':lang==='sv'?'kr':lang==='da'?'kr':'kr';
    const num = Math.round(n || 0).toLocaleString();
    return lang==='en'?`${currency}${num}`:`${num} ${currency}`;
  };

  const now = new Date();

  const getDaysUntil = () => {
    if (!payDay) return new Date(now.getFullYear(), now.getMonth()+1, 0).getDate();
    const day = parseInt(payDay);
    let next = new Date(now.getFullYear(), now.getMonth(), day);
    if (next <= now) next = new Date(now.getFullYear(), now.getMonth()+1, day);
    return Math.max(1, Math.ceil((next - now) / (1000*60*60*24)));
  };

  const daysUntil = getDaysUntil();
  const hasPayday = !!payDay;
  const incVal = parseFloat(income) || 0;
  const expVal = parseFloat(expenses) || 0;
  const dailyBudget = parseFloat(income) > 0 ? Math.round(Math.max(0, incVal - expVal) / daysUntil) : 0;

  const goNext = () => {
    setVisible(false);
    setTimeout(() => { setStep(s => s+1); setVisible(true); }, 200);
  };
  const goBack = () => {
    setVisible(false);
    setTimeout(() => { setStep(s => s-1); setVisible(true); }, 200);
  };

  const finish = async () => {
    if (finishing) return;
    setFinishing(true);
    
    try {
      dispatch({ type:'SET_LANGUAGE', payload: lang });
      if (name) dispatch({ type:'SET_NAME', payload: name });
      const pdInt = payDay ? parseInt(payDay) : null;
      const incNum = parseFloat(income) || 0;
      const expNum = parseFloat(expenses) || 0;
      dispatch({ type:'UPDATE_INCOME', payload: { income: incNum, expenses: expNum, payDay: pdInt } });
      if (pdInt) dispatch({ type:'SET_PAY_DAY', payload: pdInt });

      const goal = selectedGoal || GOALS[2];
      const goalName = lang==='en'?goal.name:lang==='sv'?goal.name_sv:lang==='da'?goal.name_da:goal.name_no;
      const goalObj = { id:Date.now(), name:goalName, target:0, saved:0, emoji:goal.emoji, color:goal.color };
      dispatch({ type:'ADD_GOAL', payload: goalObj });

      // Set username and avatar in state immediately
      dispatch({ type: 'UPDATE_USER', payload: { 
        username: name, 
        avatar: avatar || '🌱',
        avatar_emoji: avatar || '🌱'
      }});

      // Apply theme immediately
      try {
        const themes = ['sapphire','velvet','lilac','garnet','golden','moondust','neon','aurora','obsidian'];
        themes.forEach(t => document.body.classList.remove(`theme-${t}`));
        document.body.classList.add(`theme-${chosenTheme || state?.theme || 'sapphire'}`);
      } catch(e) {}

      // Save to Supabase in background - never await!
      if (state.user?.id) {
        // Save avatar + username to session for immediate use
          try { sessionStorage.setItem('savely_avatar', avatar || '🌱'); } catch(e) {}
          // Save theme
          const themes = ['sapphire','velvet','lilac','garnet','golden','moondust','neon','aurora','obsidian'];
          themes.forEach(th => document.body.classList.remove('theme-'+th));
          document.body.classList.add('theme-'+(chosenTheme||state?.theme||'sapphire'));
          dispatch({ type:'SET_THEME', payload: chosenTheme || state?.theme || 'sapphire' });
          const userId = state?.user?.id;
          if (userId) {
            supabase.from('profiles').upsert({
              id: userId,
              name: name || state?.user?.name || '',
              username: name || state?.user?.name || '',
              avatar_emoji: avatar || '🌱',
              theme: chosenTheme || state?.theme || 'sapphire',
              income: parseFloat(income) || 0,
              expenses: parseFloat(expenses) || 0,
              balance: 0, xp: 0, streak: 0, level: 1,
              daily_budget: dailyBudget, daily_spent: 0,
              plan: 'free',
              language: lang,
              last_reset_date: new Date().toDateString(),
              pay_day: payDay ? parseInt(payDay) : null,
            }).then(() => {}).catch((e) => console.error('Profile save error:', e));
          }
        supabase.from('goals').upsert({
          id: String(goalObj.id), user_id: state.user.id,
          name: goalName, target: 0, saved: 0,
          emoji: goalObj.emoji, color: goalObj.color,
        }).then(() => {}).catch(() => {});
      }
    } catch(e) {
      console.error('Finish error:', e);
    }
    
    // Always show wow and let user in
    setShowWow(true);
    setFinishing(false);
  };

  const bgGrad = 'linear-gradient(145deg, #05020F 0%, #1A0840 35%, #0D1535 65%, #050A20 100%)';
  const accent = '#00D4FF';
  const accent2 = '#B44FFF';

  if (showWow) return (
    <div style={{ minHeight:'100dvh', background:bgGrad || 'linear-gradient(145deg, #05020F 0%, #1A0840 35%, #0D1535 65%, #050A20 100%)', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', padding:'2rem', textAlign:'center' }}>
      <div style={{ fontSize:80, marginBottom:24 }}>🎉</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'#fff', marginBottom:8 }}>{tx.wow}</div>
      <div style={{ fontSize:14, color:'rgba(255,255,255,0.5)', marginBottom:32 }}>{tx.wowSub}</div>
      <div style={{ background:'rgba(255,255,255,0.06)', border:`1px solid ${accent}30`, borderRadius:20, padding:'1.5rem 2rem', marginBottom:32, width:'100%', maxWidth:300 }}>
        <div style={{ fontFamily:'var(--font-display)', fontSize:56, fontWeight:900, background:`linear-gradient(135deg,${accent},${accent2})`, WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>
          {fmtAmt(dailyBudget)}
        </div>
        <div style={{ fontSize:16, color:'rgba(255,255,255,0.6)', marginTop:4 }}>{tx.perDay}</div>
        <div style={{ fontSize:12, color:'rgba(255,255,255,0.3)', marginTop:8 }}>
          {hasPayday ? `${daysUntil} ${tx.daysToPayday}` : tx.perMonth}
        </div>
      </div>
      <button onClick={() => { console.log('onDone called'); onDone(); }} style={{ padding:'16px 40px', background:`linear-gradient(135deg,${accent},${accent2})`, color:'#fff', border:'none', borderRadius:999, fontSize:17, fontWeight:800, cursor:'pointer' }}>
        {tx.letsGo}
      </button>
    </div>
  );

  const stepAnimStyle = {
    animation: 'stepSlideIn 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
  };
  const stepStyle = {
    minHeight:'100dvh', background:bgGrad,
    display:'flex', flexDirection:'column', padding:'56px 24px 80px',
    opacity: visible?1:0, transform: visible?'translateY(0)':'translateY(20px)',
    transition:'all 0.2s ease', overflowY:'auto', boxSizing:'border-box',
  };
  const inputStyle = {
    width:'100%', padding:'16px', fontSize:18, fontWeight:600,
    background:'rgba(255,255,255,0.08)', border:`1.5px solid ${accent}40`,
    borderRadius:16, color:'#fff', outline:'none', boxSizing:'border-box',
  };
  const btnStyle = {
    width:'100%', padding:18, background:`linear-gradient(135deg,${accent},${accent2})`,
    color:'#fff', border:'none', borderRadius:999, fontSize:17, fontWeight:800,
    cursor:'pointer', marginTop:24, boxShadow:`0 8px 32px ${accent}40`,
  };

  const Progress = () => (
    <div style={{ display:'flex', gap:8, justifyContent:'center', marginBottom:40 }}>
      {[0,1,2,3].map(i => (
        <div key={i} style={{ width:i===step?32:8, height:8, borderRadius:999, background:i===step?accent:'rgba(255,255,255,0.15)', transition:'all 0.3s' }}/>
      ))}
    </div>
  );

  const BackBtn = () => step > 0 ? (
    <button onClick={goBack} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.4)', fontSize:14, cursor:'pointer', marginBottom:16, alignSelf:'flex-start', padding:0 }}>
      {tx.back}
    </button>
  ) : null;

  // Step 1
  if (step === 0) return (
    <div style={stepStyle}>
      <Progress/>
      <BackBtn/>
      <div className="savvy-bounce" style={{ textAlign:'center', marginBottom:16 }}>
        <img style={{ display:'none' }}/>
      </div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'#fff', marginBottom:8 }}>{tx.step1Title}</div>
      <div style={{ fontSize:14, color:'rgba(255,255,255,0.5)', marginBottom:32 }}>{tx.step1Sub}</div>
      <input value={name} onChange={e=>setName(e.target.value)} placeholder={tx.namePlaceholder} style={inputStyle} autoFocus maxLength={20}/>
      <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginTop:24, marginBottom:12 }}>{tx.chooseAvatar}</div>
      <div style={{ display:'flex', flexWrap:'wrap', gap:10 }}>
        {AVATARS.map(a => (
          <button key={a} onClick={() => setAvatar(a)} style={{ fontSize:28, background:avatar===a?`${accent}30`:'rgba(255,255,255,0.06)', border:`2px solid ${avatar===a?accent:'transparent'}`, borderRadius:12, padding:8, cursor:'pointer' }}>
            {a}
          </button>
        ))}
      </div>
      <button onClick={goNext} disabled={!name.trim()} style={{ ...btnStyle, opacity:name.trim()?1:0.4 }}>
        {tx.next}
      </button>
    </div>
  );

  // Step 2
  if (step === 1) return (
    <div style={stepStyle}>
      <Progress/>
      <BackBtn/>
      <div style={{ textAlign:'center', marginBottom:8, fontSize:80 }}>
        💰
      </div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'#fff', marginBottom:8 }}>{tx.step2Title}</div>
      <div style={{ fontSize:14, color:'rgba(255,255,255,0.5)', marginBottom:32 }}>{tx.step2Sub}</div>
      <input value={income} onChange={e=>setIncome(e.target.value)} placeholder={tx.incomePlaceholder} type="number" style={inputStyle} autoFocus/>
      <div style={{ marginTop:20 }}>
        <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:8 }}>{tx.step2PayDay}</div>
        <input value={payDay} onChange={e => { const v = e.target.value; if (v === '' || (parseInt(v) >= 1 && parseInt(v) <= 31)) setPayDay(v); }} placeholder={tx.step2PayDayHint} type="number" min="1" max="31" min="1" max="31" style={{ ...inputStyle, fontSize:16 }}/>
      </div>
      {parseFloat(income) > 0 && (() => {
        const inc2 = parseFloat(income);
        const days = getDaysUntil();
        const daily = Math.round(inc2 / days);
        const daysList = [];
        for (let i = 0; i < Math.min(days, 12); i++) {
          const d = new Date(now); d.setDate(now.getDate() + i);
          const isPayday = hasPayday && i === days - 1;
          daysList.push({ day: d.getDate(), isToday: i===0, isPayday });
        }
        return (
          <div style={{ marginTop:16, background:'rgba(255,255,255,0.05)', border:'1px solid rgba(0,212,255,0.2)', borderRadius:16, padding:'1rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
              <div style={{ fontSize:12, color:'rgba(255,255,255,0.5)' }}>{hasPayday ? `${days} ${tx.daysToPayday}` : tx.perMonth}</div>
              <div style={{ fontSize:16, fontWeight:800, color:'#00D4FF' }}>{fmtAmt(daily)}{tx.perDay}</div>
            </div>
            <div style={{ display:'flex', gap:4, flexWrap:'wrap' }}>
              {daysList.map((d,i) => (
                <div key={i} style={{ width:28, height:28, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, background:d.isToday?'#00D4FF':d.isPayday?'#22C97A':'rgba(255,255,255,0.06)', color:d.isToday||d.isPayday?'#fff':'rgba(255,255,255,0.4)' }}>
                  {d.isPayday?'💰':d.day}
                </div>
              ))}
            </div>
          </div>
        );
      })()}
      <button onClick={goNext} disabled={!income} style={{ ...btnStyle, opacity:income?1:0.4 }}>{tx.next}</button>
      <button onClick={goNext} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', fontSize:13, cursor:'pointer', marginTop:12 }}>{tx.skip}</button>
    </div>
  );

  // Step 3
  if (step === 2) return (
    <div style={stepStyle}>
      <Progress/>
      <BackBtn/>
      <div style={{ fontSize:40, marginBottom:16 }}>🏠</div>
      <div style={{ fontFamily:'var(--font-display)', fontSize:28, fontWeight:800, color:'#fff', marginBottom:8 }}>{tx.step3Title}</div>
      <div style={{ fontSize:14, color:'rgba(255,255,255,0.5)', marginBottom:32 }}>{tx.step3Sub}</div>
      <input value={expenses} onChange={e=>setExpenses(e.target.value)} placeholder={tx.expensesPlaceholder} type="number" style={inputStyle} autoFocus/>
      {parseFloat(income) > 0 && (
        <div style={{ marginTop:12, padding:'12px 16px', background:'rgba(0,212,255,0.08)', borderRadius:12 }}>
          <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:4 }}>🎯 {tx.step3Hint}</div>
          <div style={{ fontSize:22, fontWeight:800, color:accent }}>{fmtAmt(dailyBudget)}{tx.perDay}</div>
          <div style={{ fontSize:11, color:'rgba(255,255,255,0.3)', marginTop:4 }}>
            {hasPayday ? `${daysUntil} ${tx.daysToPayday}` : tx.perMonth}
          </div>
        </div>
      )}
      <button onClick={goNext} style={btnStyle}>{tx.next}</button>
      <button onClick={goNext} style={{ background:'none', border:'none', color:'rgba(255,255,255,0.3)', fontSize:13, cursor:'pointer', marginTop:12 }}>{tx.skip}</button>
    </div>
  );

  // Step 4 - Goals
  if (step === 3) return (
    <div style={{ ...stepStyle, paddingBottom:'calc(env(safe-area-inset-bottom,16px) + 100px)' }}>
      <Progress/>
      <BackBtn/>
      <div style={{ fontFamily:'var(--font-display)', fontSize:24, fontWeight:800, color:'#fff', marginBottom:8 }}>{tx.step4Title}</div>
      <div style={{ fontSize:13, color:'rgba(255,255,255,0.5)', marginBottom:20 }}>{tx.step4Sub}</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
        {GOALS.map(g => {
          const goalName = lang==='en'?g.name:lang==='sv'?g.name_sv:lang==='da'?g.name_da:g.name_no;
          const selected = selectedGoal?.emoji === g.emoji;
          return (
            <button key={g.emoji} onClick={() => { if(g.emoji==='🎯'){setShowCustomGoal(true);setSelectedGoal(g);}else{setShowCustomGoal(false);setSelectedGoal(g);} }} style={{ padding:'20px 12px', background:selected?`${g.color}22`:'rgba(255,255,255,0.05)', border:`2px solid ${selected?g.color:'rgba(255,255,255,0.1)'}`, borderRadius:16, cursor:'pointer', textAlign:'center', transition:'all 0.2s' }}>
              <div style={{ fontSize:32, marginBottom:8 }}>{g.emoji}</div>
              <div style={{ fontSize:13, fontWeight:600, color:'#fff' }}>{goalName}</div>
            </button>
          );
        })}
      </div>
      {showCustomGoal && (
        <div style={{ background:'rgba(255,255,255,0.05)', borderRadius:16, padding:16, marginBottom:16 }}>
          <input value={customGoalName} onChange={e=>setCustomGoalName(e.target.value)}
            placeholder={lang==='en'?'Goal name':lang==='sv'?'Målnamn':lang==='da'?'Målnavn':'Målnavn'}
            style={{ ...inputStyle, marginBottom:10, fontSize:15 }}/>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.4)', marginBottom:8 }}>
            {lang==='en'?'Choose color:':lang==='sv'?'Välj färg:':lang==='da'?'Vælg farve:':'Velg farge:'}
          </div>
          <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginTop:8 }}>
            {['#22C97A','#FF6B6B','#B44FFF','#F5C518','#FB923C','#00D4FF','#FF9500','#FF2D55','#5856D6','#34AADC','#4CD964','#FF3B30'].map(color => (
              <button key={color} onClick={() => setCustomGoalColor(color)}
                style={{ width:44, height:44, borderRadius:'50%', background:color, border:`4px solid ${customGoalColor===color?'#fff':'transparent'}`, cursor:'pointer', boxShadow:customGoalColor===color?`0 0 16px ${color}80`:'none', transition:'all 0.2s' }}/>
            ))}
          </div>
        </div>
      )}
      <button onClick={finish} disabled={finishing} style={{ ...btnStyle, opacity:finishing?0.7:1 }}>
        {finishing ? '...' : tx.getStarted}
      </button>
    </div>
  );

  return null;
}
